/**
 * O `data` do envelope `{ success, data }` que as rotas do aluno devolvem.
 *
 * O tipo do Eden (tipado desde a D1) é a UNIÃO do sucesso com os ramos de erro
 * que a rota devolve pelo mesmo `return` — `{ success: false, error }`, o
 * `{ error }` do gate. Até a D1b as telas liam `(x as any)?.data`, que além do
 * ramo de erro desligava a checagem do sucesso. Aqui o comportamento é o mesmo
 * (ramo sem `data` → `undefined`), e o tipo do `data` é o do contrato.
 */
export type EnvelopeData<T> = T extends { data: infer D } ? D : never

export function envelopeData<T>(body: T): EnvelopeData<T> | undefined {
  if (body !== null && typeof body === 'object' && 'data' in body) {
    return (body as { data: EnvelopeData<T> }).data
  }
  return undefined
}

/**
 * O corpo de SUCESSO de uma resposta, sem os ramos de erro que o tipo do Eden
 * arrasta (`{ success: false, error }`, `{ error }` do gate). Mesma regra do
 * `successBody` de `@buu/api-client` — cópia de propósito: o aluno não depende
 * do pacote. Um 2xx que chegue com `error: string` vira exceção no formato do
 * erro do Eden (`{ status, value }`), em vez de ser lido como dado.
 */
export function successBody<T>(data: T): Exclude<T, { error: string }> {
  if (data !== null && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string') {
    throw { status: 200, value: data }
  }
  return data as Exclude<T, { error: string }>
}
